import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Plus, 
  Play, 
  Trash2, 
  ArrowLeft, 
  Calendar,
  Clock,
  BookOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSubscription } from '../context/SubscriptionContext';
import UpgradeModal from '../components/UpgradeModal';
import { toast } from 'sonner';
import './FlashcardHub.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FlashcardHub = () => {
  const navigate = useNavigate();
  const { trackPlay, isPremium, canPlayMore, playedGamesCount, maxPlayedGames } = useSubscription();
  const [savedSets, setSavedSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const loadSavedSets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/flashcards`, { withCredentials: true });
      setSavedSets(response.data || []);
    } catch (error) {
      console.error('Error loading flashcard sets:', error);
      toast.error('Failed to load flashcard sets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSavedSets();
  }, []);

  const createNewSet = () => {
    navigate('/flashcard-builder');
  };

  const playSet = async (setId) => {
    // Check play limits for free users
    if (!isPremium && !canPlayMore) {
      setShowUpgradeModal(true);
      return;
    }

    // Track the play
    const result = await trackPlay(`flashcard-${setId}`, 'flashcard');
    if (!result.success && result.limitReached) {
      setShowUpgradeModal(true);
      return;
    }

    navigate(`/flashcard-game?setId=${setId}`);
  };

  const deleteSet = async (setId) => {
    try {
      await axios.delete(`${API}/flashcards/${setId}`, { withCredentials: true });
      loadSavedSets();
      setShowDeleteConfirm(null);
      toast.success('Flashcard set deleted successfully');
    } catch (error) {
      console.error('Error deleting flashcard set:', error);
      toast.error('Failed to delete flashcard set');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const isPlayDisabled = !isPremium && !canPlayMore;

  return (
    <div className="flashcard-hub-page" data-testid="flashcard-hub">
      <div className="flashcard-hub-container">
        <div className="hub-header">
          <Button 
            onClick={() => navigate('/dashboard')} 
            className="back-button"
            variant="ghost"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            Back
          </Button>
          <div className="hub-title">
            <div className="title-with-icon">
              <div className="hub-icon">📚</div>
              <div>
                <h1>Flashcard Sets</h1>
                <p>Create and study your flashcards</p>
              </div>
            </div>
          </div>
        </div>

        <motion.div
          className="create-new-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Button onClick={createNewSet} className="create-new-btn" data-testid="create-new-btn">
            <Plus size={24} />
            Create New Game
          </Button>
        </motion.div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading flashcard sets...</p>
          </div>
        ) : (
          <div className="sets-grid">
            {savedSets.map((set, index) => (
                <motion.div
                  key={set.id}
                  className="set-card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`set-card-${index}`}
                >
                  <div className="set-card-header">
                    <div className="set-icon">📚</div>
                    <div className="set-info">
                      <h3 className="set-title">{set.title || 'Untitled Set'}</h3>
                      <div className="set-meta">
                        <span className="meta-item">
                          <Calendar size={14} />
                          {formatDate(set.created_at)}
                        </span>
                        <span className="meta-item">
                          <Clock size={14} />
                          {formatTime(set.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="set-details">
                    <p className="set-count">🃏 {set.cards?.length || 0} cards</p>
                    {set.timer_setting !== 'off' && (
                      <p className="set-timer">⏱️ Timer: {set.timer_setting}</p>
                    )}
                  </div>

                  <div className="set-card-actions">
                    <Button 
                      onClick={() => playSet(set.id)} 
                      className={`play-btn ${isPlayDisabled ? 'disabled' : ''}`}
                      disabled={isPlayDisabled}
                      data-testid={`play-btn-${index}`}
                    >
                      <Play size={18} />
                      {isPlayDisabled ? 'Limit Reached' : 'Study'}
                    </Button>
                    <Button 
                      onClick={() => setShowDeleteConfirm(set.id)} 
                      className="delete-btn"
                      variant="ghost"
                      data-testid={`delete-btn-${index}`}
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </motion.div>
              ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            className="delete-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDeleteConfirm(null)}
          >
            <motion.div
              className="delete-dialog"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Delete Flashcard Set?</h3>
              <p>This action cannot be undone.</p>
              <div className="delete-actions">
                <Button 
                  onClick={() => setShowDeleteConfirm(null)} 
                  variant="ghost"
                  data-testid="cancel-delete-btn"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => deleteSet(showDeleteConfirm)} 
                  className="confirm-delete-btn"
                  data-testid="confirm-delete-btn"
                >
                  Delete
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        limitType="play"
        currentCount={playedGamesCount}
        maxCount={maxPlayedGames || 7}
      />
    </div>
  );
};

export default FlashcardHub;
